import { useState } from "react";
import { toast } from "sonner";
import { fileService } from "@/services/ServiceRegistry";
import {
  checkInOutAvailabilityFromDisplayRow,
  CreateHotelServiceFormData,
  mapServiceDisplaySettingsToRequest,
} from "../utils/serviceSchema";
import { UpdateHotelServiceRequest } from "@/types/hotelService";
import { useUpdateHotelService } from "@/hooks/useHotelServices";

export const useServiceUpdate = (id: string, hotelId: string) => {
  const updateHotelServiceMutation = useUpdateHotelService(id, hotelId);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleHotelServiceUpdate = async (
    data: CreateHotelServiceFormData,
    selectedPhotoFile: File | null,
  ) => {
    try {
      setIsLoading(true);
      const {
        price,
        discount,
        photoId,
        contractor,
        serviceDisplaySettings,
        ...serviceData
      } = data;

      const { checkinAvailable, checkoutAvailable } =
        checkInOutAvailabilityFromDisplayRow(
          serviceDisplaySettings.checkInCheckOut,
        );

      let uploadedPhotoId = photoId;

      if (selectedPhotoFile) {
        try {
          uploadedPhotoId =
            await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
        } catch (uploadError) {
          console.error("Failed to upload photo:", uploadError);
          toast.error("Photo upload failed");
        }
      }

      const updateRequest: UpdateHotelServiceRequest = {
        ...serviceData,
        checkinAvailable,
        checkoutAvailable,
        displaySettings: mapServiceDisplaySettingsToRequest(
          serviceDisplaySettings,
        ),
        ...(uploadedPhotoId && { photoId: uploadedPhotoId }),
        ...(price && { price: parseFloat(price) }),
        ...(discount && { discount: parseFloat(discount) }),
        ...(contractor && {
          contractor: {
            ...contractor,
            fee: parseFloat(contractor.fee),
          },
        }),
        staffId: data?.teamId || !data?.staffId ? null : data?.staffId,
        teamId: data.staffId || !data.teamId ? null : data.teamId,
      };

      await updateHotelServiceMutation.mutateAsync(updateRequest);

      toast.success("Hotel Service updated successfully!");
    } catch (err) {
      const message = (err as Error).message || "Update failed";
      setError(message);
      toast.error(message);
      console.error("Error updating hotel service:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleHotelServiceUpdate,
    isLoading,
    error,
  };
};
